import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Rocket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useOnboardingRuns } from '@/hooks/useOnboarding'
import { NewOnboardingDialog } from '@/components/onboarding/NewOnboardingDialog'

const RUN_STATUS_VARIANT: Record<string, string> = {
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  abandoned: 'bg-muted text-muted-foreground',
}

export function OnboardingListPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data: runs, isLoading } = useOnboardingRuns()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Onboarding</h1>
          <p className="text-muted-foreground">
            Guided client onboarding runs with a live step checklist.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New onboarding
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !runs || runs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Rocket className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium">No onboarding runs yet</p>
              <p className="text-sm text-muted-foreground">
                Start one with the “New onboarding” button.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>
                      <Link
                        to={`/admin/onboarding/${run.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {run.accounts?.name ?? run.account_id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`border-transparent ${RUN_STATUS_VARIANT[run.status] ?? ''}`}
                      >
                        {run.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {run.created_at
                        ? new Date(run.created_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {run.completed_at
                        ? new Date(run.completed_at).toLocaleString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewOnboardingDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
